'use client';

import { motion, type Variants } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import type { StaticImageData } from 'next/image';

import { customRichTextTags } from '@/utils/i18n';

import avatar3billion from '@images/testimonial-avatar-3billion.webp';
import avatarLee from '@images/testimonial-avatar-lee.webp';
import avatarAldo from '@images/testimonial-avatar-aldo.webp';

// 채팅형 말풍선. 프롬프트(왼쪽, 3billion)와 추천사(오른쪽, 의료진)를 side로 구분해 하나로 렌더링
type ChatItem =
  | { key: string; side: 'left'; avatar: StaticImageData; text: string }
  | {
      key: string;
      side: 'right';
      avatar: StaticImageData;
      quote: string;
      name: string;
      specialty: string;
    };

// 섹션 진입 시 한 번 트리거 → 자식별 delay로 순차 등장.
// 기본 영역(헤딩·프롬프트) 먼저 다 보인 뒤 추천사 1 → 2.
const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: (delay: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut', delay },
  }),
};

function ChatBubble({ item, delay }: { item: ChatItem; delay: number }) {
  const locale = useLocale();
  const leftBubbleMobilePadding = locale === 'ko' ? 'px-[12px]' : 'px-[24px]';
  const leftBubbleMobileLetterSpacing =
    locale === 'ko' ? 'tracking-[-0.376px]' : 'tracking-[-0.176px]';
  const isRight = item.side === 'right';

  // 좌/우 각각 원본 그대로의 크기·색·꼬리 위치 (프롬프트는 남은 폭에 맞춰 w-fit, 추천사는 고정 778px)
  const bubbleClass = isRight
    ? 'relative mt-[42px] w-[778px] max-w-full rounded-[16px] rounded-tr-[0px] bg-white px-[25px] py-[22px] shadow-[0_10px_30px_0_rgba(4,4,53,0.06)] md:mt-[48px] md:px-[54px] md:py-[30px]'
    : `relative mt-[24px] w-fit max-w-[calc(100%-64px)] rounded-[36px] rounded-tl-[0px] bg-[#040435] ${leftBubbleMobilePadding} py-[14px] md:mt-[48px] md:max-w-[calc(100%-128px)] md:px-[54px] md:py-[24px]`;

  // 꼬리: 말풍선과 같은 색 원을 상단 가장자리에 반쯤 걸쳐 붙임
  const tailClass = isRight
    ? 'absolute -right-[7px] top-0 size-[15px] rounded-full bg-white md:-right-[14px] md:size-[28px]'
    : 'absolute -left-[7px] top-0 size-[15px] rounded-full bg-[#040435] md:-left-[14px] md:size-[28px]';

  const avatarEl = (
    <Image
      src={item.avatar}
      alt=""
      width={100}
      height={100}
      className="size-12 shrink-0 rounded-full md:size-[100px]"
    />
  );

  return (
    <motion.div
      variants={fadeUp}
      custom={delay}
      className={`flex items-start gap-4 md:gap-7 ${isRight ? 'justify-end' : ''}`}
    >
      {!isRight && avatarEl}

      <div className={bubbleClass}>
        <span className={tailClass} />
        {item.side === 'right' ? (
          <>
            <p className="text-[14px] font-normal leading-[1.5] tracking-[-0.176px] text-[#2B2E33] md:text-[18px]">
              {item.quote}
            </p>
            <div className="mt-4 md:mt-5">
              <p className="text-[14px] font-semibold leading-6 tracking-[-0.176px] text-[#2B2E33] md:text-[16px]">
                {item.name}
              </p>
              <p className="text-[14px] font-normal leading-6 tracking-[-0.176px] text-[#A1A7B2] md:text-[16px]">
                {item.specialty}
              </p>
            </div>
          </>
        ) : (
          <p
            className={`text-[14px] font-normal leading-[1.5] ${leftBubbleMobileLetterSpacing} text-white md:text-[18px]`}
          >
            {item.text}
          </p>
        )}
      </div>

      {isRight && avatarEl}
    </motion.div>
  );
}

export default function Testimonials() {
  const t = useTranslations('Products.testimonials');

  const items: ChatItem[] = [
    { key: 'prompt', side: 'left', avatar: avatar3billion, text: t('prompt') },
    {
      key: '1',
      side: 'right',
      avatar: avatarLee,
      quote: t('items.1.quote'),
      name: t('items.1.name'),
      specialty: t('items.1.specialty'),
    },
    {
      key: '2',
      side: 'right',
      avatar: avatarAldo,
      quote: t('items.2.quote'),
      name: t('items.2.name'),
      specialty: t('items.2.specialty'),
    },
  ];
  // 헤딩(0) → 프롬프트(0.2) → 추천사 1(1) → 추천사 2(2), 기존 타이밍 그대로 유지
  const delays = [0.2, 1, 2];

  return (
    <section className="w-full bg-[#F7F8FA] py-[60px] md:px-8 md:py-[160px]">
      <motion.div
        className="mx-auto flex max-w-[1260px] flex-col gap-5 px-5 md:gap-10 md:px-0"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
      >
        <motion.h2
          variants={fadeUp}
          custom={0}
          className="text-center text-2xl font-semibold leading-8 tracking-[-0.464px] text-[#2B2E33] md:text-[48px] md:leading-[56px] md:tracking-[-1.072px]"
        >
          {t.rich('title', customRichTextTags)}
        </motion.h2>

        {/* 부모 gap으로 최소 행간을 보장 (기존엔 gap 없이 말풍선 자체 mt에만 의존해 텍스트 길이에 따라 행간이 들쭉날쭉했음) */}
        <div className="flex flex-col gap-6 md:gap-10">
          {items.map((item, idx) => (
            <ChatBubble key={item.key} item={item} delay={delays[idx]} />
          ))}
        </div>
      </motion.div>
    </section>
  );
}
